import { useState } from 'react';
import { Modal } from '../../components/Modal';
import type { FeedCategoryRead } from '../../types/api';
import type { NewsFilterPrefs } from './newsFilterStorage';
import {
  EMPTY_NEWS_FILTER,
  activeFilterCount,
  isSameFilter,
  type NewsFilter,
  type NewsSortOrder,
  type NewsSourceOption,
} from './newsFilters';

interface NewsFilterPanelProps {
  /** The filter currently narrowing the list. The panel opens on a copy of it. */
  filter: NewsFilter;
  /** The order in force. Not edited here — it rides along into the default. */
  sort: NewsSortOrder;
  /** Every provider the list can be read down to, named and filed. */
  sources: readonly NewsSourceOption[];
  /** The user's own folders. The section is absent when there are none. */
  categories: readonly FeedCategoryRead[];
  /** What this device has on file for this business, or null for none. */
  savedDefault: NewsFilterPrefs | null;
  /** Commit a filter to the list behind the panel. */
  onApply: (filter: NewsFilter) => void;
  /** Restoring a default sets the order too, so the panel can ask for it. */
  onSortChange: (sort: NewsSortOrder) => void;
  /** Write this filter, with the order in force, as the default for next time. */
  onSaveDefault: (filter: NewsFilter) => void;
  onClearDefault: () => void;
  onDismiss: () => void;
}

/** Adds or removes one id, without caring where in the list it sits. */
function toggleId(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((entry) => entry !== id) : [...ids, id];
}

/**
 * Filter the newsfeed — every narrowing decision in one place.
 *
 * Three dimensions, each of which only ever takes items away: the industry tag
 * the server put on an item, the folders the user filed their sources into, and
 * the providers themselves. They compose with an and, so a category and a
 * provider together mean "this provider, and only if it is filed there" —
 * a filter that could widen the list would make the count on the button a lie.
 *
 * Choices are held here until Apply, so a half-made decision never flickers
 * through the list behind the dialog. Clear all is the exception: it applies at
 * once, because it is the way out of a filter that shows nothing, and waiting
 * for a second click to prove it is not what that click means.
 *
 * Nothing here fetches, and nothing here spends a generation.
 */
export function NewsFilterPanel({
  filter,
  sort,
  sources,
  categories,
  savedDefault,
  onApply,
  onSortChange,
  onSaveDefault,
  onClearDefault,
  onDismiss,
}: NewsFilterPanelProps) {
  const [draft, setDraft] = useState<NewsFilter>(filter);
  // Ticked by default when a default already exists: someone who keeps one is
  // editing it, not making a one-off change they then have to save twice.
  const [saveAsDefault, setSaveAsDefault] = useState(savedDefault !== null);

  const isDefaultApplied =
    savedDefault !== null &&
    isSameFilter(filter, savedDefault.filter) &&
    sort === savedDefault.sort;
  const draftCount = activeFilterCount(draft);

  function handleApply() {
    onApply(draft);
    if (saveAsDefault) onSaveDefault(draft);
    onDismiss();
  }

  /** Straight through to the list: this is the escape from an empty screen. */
  function handleClearAll() {
    setDraft(EMPTY_NEWS_FILTER);
    onApply(EMPTY_NEWS_FILTER);
  }

  /** Back to what was saved — the order as well, since both were saved. */
  function handleReset() {
    if (savedDefault === null) return;
    setDraft(savedDefault.filter);
    onApply(savedDefault.filter);
    onSortChange(savedDefault.sort);
  }

  function handleClearDefault() {
    onClearDefault();
    setSaveAsDefault(false);
  }

  return (
    <Modal title="Filter the newsfeed" kind="filter" onDismiss={onDismiss}>
      <div className="news-filters">
        <p className="field__hint">
          Every filter narrows the headlines already on screen. None of them loads anything new.
        </p>

        <fieldset className="news-filters__group">
          <legend className="news-filters__legend">Industry match</legend>
          <label className="feed-pick">
            <input
              type="checkbox"
              className="feed-pick__box"
              checked={draft.industryOnly}
              onChange={() =>
                setDraft((current) => ({ ...current, industryOnly: !current.industryOnly }))
              }
            />
            <span className="feed-pick__body">
              <span className="feed-pick__title">Only items matched to my industry</span>
              <span className="feed-pick__note">
                The tag the feed puts on an item, not something we work out here.
              </span>
            </span>
          </label>
        </fieldset>

        {categories.length > 0 ? (
          <fieldset className="news-filters__group">
            <legend className="news-filters__legend">My categories</legend>
            <label className="feed-pick">
              <input
                type="checkbox"
                className="feed-pick__box"
                checked={draft.anyCategory}
                onChange={() =>
                  setDraft((current) => ({ ...current, anyCategory: !current.anyCategory }))
                }
              />
              <span className="feed-pick__body">
                <span className="feed-pick__title">Only sources in a category</span>
                <span className="feed-pick__note">Any folder, filed by you.</span>
              </span>
            </label>

            <ul className="news-filters__list">
              {categories.map((category) => (
                <li key={category.id}>
                  <label className="feed-pick">
                    <input
                      type="checkbox"
                      className="feed-pick__box"
                      checked={draft.categoryIds.includes(category.id)}
                      onChange={() =>
                        setDraft((current) => ({
                          ...current,
                          categoryIds: toggleId(current.categoryIds, category.id),
                        }))
                      }
                    />
                    <span className="feed-pick__body">
                      <span className="feed-pick__title">{category.name}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ) : null}

        {sources.length > 0 ? (
          <fieldset className="news-filters__group">
            <legend className="news-filters__legend">Sources</legend>
            <p className="field__hint">Nothing ticked means every source.</p>
            <ul className="news-filters__list">
              {sources.map((source) => (
                <li key={source.id}>
                  <label className="feed-pick">
                    <input
                      type="checkbox"
                      className="feed-pick__box"
                      checked={draft.sourceIds.includes(source.id)}
                      onChange={() =>
                        setDraft((current) => ({
                          ...current,
                          sourceIds: toggleId(current.sourceIds, source.id),
                        }))
                      }
                    />
                    <span className="feed-pick__body">
                      <span className="feed-pick__title">{source.label}</span>
                      {source.domain ? (
                        <span className="feed-pick__domain">{source.domain}</span>
                      ) : null}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ) : null}

        {savedDefault !== null ? (
          <div className="news-filters__default">
            <p className="news-filters__default-note">
              {isDefaultApplied
                ? 'Default applied. This is how the newsfeed opens.'
                : 'You have a saved default. It is not what is on screen.'}
            </p>
            <div className="news-filters__default-actions">
              <button className="button button--ghost" type="button" onClick={handleReset}>
                Reset to default
              </button>
              <button className="button button--ghost" type="button" onClick={handleClearDefault}>
                Clear default
              </button>
            </div>
          </div>
        ) : null}

        <div className="news-filters__foot">
          <label className="news-filters__save">
            <input
              type="checkbox"
              checked={saveAsDefault}
              onChange={() => setSaveAsDefault((current) => !current)}
            />
            <span>Save as my default</span>
          </label>
          <button
            className="button button--ghost"
            type="button"
            disabled={draftCount === 0}
            onClick={handleClearAll}
          >
            Clear all
          </button>
          <button className="button button--primary" type="button" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </Modal>
  );
}
